import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "sonner";
import { Modal, TextInput, Checkbox, Textarea } from "flowbite-react";

const PremiumJobs = () => {
  const loggedinUser = useSelector((state) => state.user);
  const [premiumJobs, setPremiumJobs] = useState([]);
  const [jobSeekers, setJobSeekers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null); // For job details
  const [selectedJobSeeker, setSelectedJobSeeker] = useState(null); // For job seeker details
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedJobSeekers, setSelectedJobSeekers] = useState([]); // For multi-select
  const [message, setMessage] = useState("");
  const [selectedJobForNotification, setSelectedJobForNotification] = useState(null);
  const [sentJobs, setSentJobs] = useState(new Set()); // Track sent job notifications

  // Fetch all jobs and job seekers
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all jobs instead of just pending ones
        const jobsResponse = await axios.get(`${process.env.REACT_APP_API_URL}job/jobs`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const allJobs = jobsResponse.data.data || [];
        setPremiumJobs(allJobs.filter(job => job.jobType === "Premium"));

        const seekersResponse = await axios.get(`${process.env.REACT_APP_API_URL}jobseeker/allJobSeeker`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setJobSeekers(seekersResponse.data.data || []);
      } catch (error) {
        console.error("Error fetching data:", error.response?.data || error.message);
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    if (loggedinUser.currentUser?.role === "superadmin" || localStorage.getItem("userType") === "superadmin") {
      fetchData();
    } else {
      toast.error("Unauthorized: Superadmin access required");
    }
  }, [loggedinUser]);

  // Filter job seekers based on search query with improved fuzzy matching using interestedJob
  const filteredJobSeekers = jobSeekers.filter(seeker => {
    if (!searchQuery) return true;
    return seeker.interestedJob?.some(interestedJob =>
      interestedJob.toLowerCase().includes(searchQuery.toLowerCase()) ||
      fuzzyMatch(interestedJob, searchQuery)
    );
  });

  // Improved fuzzy matching function
  const fuzzyMatch = (str, query) => {
    str = str.toLowerCase();
    query = query.toLowerCase();
    let j = 0;
    for (let i = 0; i < str.length && j < query.length; i++) {
      if (str[i] === query[j]) j++;
    }
    return j >= query.length * 0.7; // Allow partial matches (70% match threshold)
  };

  // Handle job selection for notification
  const handleJobSelect = (job) => {
    setSelectedJobForNotification(job);
    setMessage(`Dear Job Seeker,\n\nWe have a new Premium job opportunity: ${job.jobTitle}\nDescription: ${job.jobDescription?.join("\n") || "No description"}\nStatus: ${job.status}\n\nBest regards,\nSuperAdmin`);
  };

  // Send notification via backend API
  const sendNotification = async () => {
    if (!selectedJobForNotification || !message || selectedJobSeekers.length === 0) {
      toast.error("Please select a job, provide a message, and choose job seekers");
      return;
    }

    const jobSeekerIds = selectedJobSeekers.map(seeker => seeker._id);
    const sentJobKeys = Array.from(sentJobs).filter(key => key.startsWith(selectedJobForNotification._id));

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}send-job-notifications`,
        {
          jobId: selectedJobForNotification._id,
          jobSeekerIds,
          message,
          sentJobs: sentJobKeys,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      toast.success(response.data.message);
      setSentJobs(prev => {
        const newSentJobs = new Set(prev);
        selectedJobSeekers.forEach(seeker => newSentJobs.add(`${seeker._id}-${selectedJobForNotification._id}`));
        return newSentJobs;
      });
      setSelectedJobSeekers([]);
      setMessage("");
      setSelectedJobForNotification(null);
    } catch (error) {
      console.error("Error sending notifications:", error.response?.data || error.message);
      toast.error("Failed to send notifications");
    }
  };

  const myloggedinUser = localStorage.getItem("userType");

  if (myloggedinUser !== "superadmin") {
    return <div className="p-6 text-red-600">Access denied: Superadmin only</div>;
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h2 className="text-2xl font-bold mb-4">Premium Jobs Dashboard</h2>

      {/* Search Input */}
      <input
        type="text"
        className="mb-4 w-full md:w-1/3 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Search interested jobs..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      {/* Premium Jobs Table */}
      {loading ? (
        <div>Loading...</div>
      ) : premiumJobs.length === 0 ? (
        <div>No premium jobs available</div>
      ) : (
        <table className="w-full hoverable">
          <thead>
            <tr className="bg-gray-200">
              <th className="p-2 text-left">Title</th>
              <th className="p-2 text-left">Employer</th>
              <th className="p-2 text-left">Location</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {premiumJobs.map((job) => (
              <tr key={job._id} className="bg-white border-b">
                <td className="p-2">{job.jobTitle || "Untitled"}</td>
                <td className="p-2">{job.employer.companyName}</td>
                <td className="p-2">{`${job.city}, ${job.state}`}</td>
                <td className="p-2">{job.status || "Unknown"}</td>
                <td className="p-2">
                  <button
                    className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                    onClick={() => {
                      handleJobSelect(job);
                      setSelectedJob(job); // Set for View More if needed
                    }}
                  >
                    Select for Notification / View More
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Job Seekers Table */}
      {loading ? (
        <div>Loading...</div>
      ) : filteredJobSeekers.length === 0 ? (
        <div>No job seekers match the search</div>
      ) : (
        <table className="w-full hoverable mt-6">
          <thead>
            <tr className="bg-gray-200">
              <th className="p-2 text-left">Name</th>
              <th className="p-2 text-left">Email</th>
              <th className="p-2 text-left">Interested Jobs</th>
              <th className="p-2 text-left">Select</th>
              <th className="p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredJobSeekers.map((seeker) => (
              <tr key={seeker._id} className="bg-white border-b">
                <td className="p-2">{seeker.name || "Unnamed"}</td>
                <td className="p-2">{seeker.email || "No email"}</td>
                <td className="p-2">{seeker.interestedJob?.join(", ") || "None"}</td>
                <td className="p-2">
                  <input
                    type="checkbox"
                    checked={selectedJobSeekers.some(s => s._id === seeker._id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedJobSeekers([...selectedJobSeekers, seeker]);
                      } else {
                        setSelectedJobSeekers(selectedJobSeekers.filter(s => s._id !== seeker._id));
                      }
                    }}
                    className="mr-2"
                  />
                </td>
                <td className="p-2">
                  <button
                    className="bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-600"
                    onClick={() => setSelectedJobSeeker(seeker)}
                  >
                    View More
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Job Details Modal */}
      <Modal show={!!selectedJob} onClose={() => setSelectedJob(null)}>
        <Modal.Header>Job Details</Modal.Header>
        <Modal.Body>
          {selectedJob && (
            <div className="space-y-4">
              <p><strong>Title:</strong> {selectedJob.jobTitle || "Untitled"}</p>
              <p><strong>Employer:</strong> {selectedJob.employer.companyName}</p>
              <p><strong>Job Type:</strong> {selectedJob.jobType}</p>
              <p><strong>Location:</strong> {`${selectedJob.city}, ${selectedJob.localGovernment}, ${selectedJob.state}`}</p>
              <p><strong>Views:</strong> {selectedJob.uniqueViewers}</p>
              <p><strong>Status:</strong> {selectedJob.status || "Unknown"}</p>
              <p><strong>Description:</strong> {selectedJob.jobDescription?.join(", ") || "No description"}</p>
              <p><strong>Skills:</strong> {selectedJob.jobSkills?.join(", ") || "No skills"}</p>
              <p><strong>Tags:</strong> {selectedJob.jobTag?.join(", ") || "No tags"}</p>
              <p><strong>Salary:</strong> {selectedJob.salary || "Not specified"}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
            onClick={() => setSelectedJob(null)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* Job Seeker Details Modal */}
      <Modal show={!!selectedJobSeeker} onClose={() => setSelectedJobSeeker(null)}>
        <Modal.Header>Job Seeker Details</Modal.Header>
        <Modal.Body>
          {selectedJobSeeker && (
            <div className="space-y-4">
              <p><strong>Name:</strong> {selectedJobSeeker.name || "Unnamed"}</p>
              <p><strong>Email:</strong> {selectedJobSeeker.email || "No email"}</p>
              <p><strong>Job Choices:</strong> {selectedJobSeeker.jobChoice?.join(", ") || "No choices"}</p>
              <p><strong>Interested Jobs:</strong> {selectedJobSeeker.interestedJob?.join(", ") || "None"}</p>
              <p><strong>Resume:</strong> {selectedJobSeeker.resume?.join(", ") || "No resume"}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
            onClick={() => setSelectedJobSeeker(null)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* Notification Modal */}
      <Modal show={!!selectedJobForNotification} onClose={() => { setSelectedJobForNotification(null); setMessage(""); }}>
        <Modal.Header>Send Job Notification</Modal.Header>
        <Modal.Body>
          <p><strong>Job:</strong> {selectedJobForNotification?.jobTitle}</p>
          <p><strong>Status:</strong> {selectedJobForNotification?.status || "Unknown"}</p>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Edit your message here..."
            className="w-full mb-4"
            style={{ height: "200px" }} // Increased height
          />
          <button
            className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
            onClick={sendNotification}
          >
            Send to Selected Job Seekers
          </button>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
            onClick={() => { setSelectedJobForNotification(null); setMessage(""); }}
          >
            Cancel
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PremiumJobs;